/** product.yaml annotations written by the product_master change-request script. */
export const PRODUCT_MASTER_PRODUCT_ID_ANNOTATION =
  'moneyforward.com/product-id';
export const PRODUCT_MASTER_CREATED_AT_ANNOTATION =
  'moneyforward.com/created-at';
export const PRODUCT_MASTER_UPDATED_AT_ANNOTATION =
  'moneyforward.com/updated-at';

export type ProductMasterRecordDateField = 'created' | 'updated';

/**
 * Display value for one record date.
 * - `exact`: calendar date (UTC) of the stored timestamp.
 * - `monthOrEarlier`: created date is unknown; the product existed by the end of `month`.
 */
export type ProductMasterRecordDate =
  | { kind: 'exact'; date: string }
  | { kind: 'monthOrEarlier'; month: string };

/** Inclusive `YYYY-MM-DD` bounds; either side may be omitted. */
export interface ProductMasterRecordDateRange {
  from?: string;
  to?: string;
}

/** Seconds required; at most millisecond precision so every runtime reads the same instant. */
const ISO_TIMESTAMP =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?(Z|[+-](\d{2}):(\d{2}))$/;
/** java.time.ZoneOffset limit, so DFP ingest accepts everything Backstage shows. */
const MAX_OFFSET_MINUTES = 18 * 60;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function pad4(n: number): string {
  return String(n).padStart(4, '0');
}

/**
 * Date.UTC maps years 0–99 onto 1900–1999. Build from a year outside that range,
 * then set the real year, so 0001-01-01 stays year 1 and invalid days still roll over.
 */
function utcFromParts(
  year: number,
  monthIndex: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): Date {
  const basis = year < 100 ? year + 10000 : year;
  const date = new Date(Date.UTC(basis, monthIndex, day, hour, minute, second));
  if (year < 100) {
    date.setUTCFullYear(year);
  }
  return date;
}

function utcMonth(d: Date): string {
  return `${pad4(d.getUTCFullYear())}-${pad2(d.getUTCMonth() + 1)}`;
}

function utcDate(d: Date): string {
  return `${utcMonth(d)}-${pad2(d.getUTCDate())}`;
}

export function parseProductMasterRecordTimestamp(
  raw: unknown,
): Date | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const text = raw.trim();
  const m = ISO_TIMESTAMP.exec(text);
  if (!m) {
    return undefined;
  }
  const [year, month, day, hour, minute, second] = m.slice(1, 7).map(Number);
  const wall = utcFromParts(year, month - 1, day, hour, minute, second);
  const offsetHour = m[8] === undefined ? 0 : Number(m[8]);
  const offsetMinute = m[9] === undefined ? 0 : Number(m[9]);
  if (
    wall.getUTCFullYear() !== year ||
    wall.getUTCMonth() !== month - 1 ||
    wall.getUTCDate() !== day ||
    wall.getUTCHours() !== hour ||
    wall.getUTCMinutes() !== minute ||
    wall.getUTCSeconds() !== second ||
    offsetMinute > 59 ||
    offsetHour * 60 + offsetMinute > MAX_OFFSET_MINUTES
  ) {
    return undefined;
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/**
 * Backfill placeholder. List, CSV, and spreadsheet show `0001-01-01` and `2026-05-01`
 * as "2026-05 or earlier" for both created and updated.
 */
const UNKNOWN_RECORD_DISPLAY_MONTH = '2026-05';

function isUnknownRecordPlaceholder(d: Date): boolean {
  const midnightOnFirst =
    d.getUTCDate() === 1 &&
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;
  if (!midnightOnFirst) {
    return false;
  }
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  // 0001-01-01 is the current backfill. 2026-05-01 is the value already in the catalog.
  return (year === 1 && month === 0) || (year === 2026 && month === 4);
}

export function isProductMasterCreatedMonthBound(d: Date): boolean {
  return (
    d.getUTCDate() === 1 &&
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  );
}

export function resolveProductMasterRecordDate(
  raw: unknown,
  field: ProductMasterRecordDateField,
): ProductMasterRecordDate | undefined {
  const parsed = parseProductMasterRecordTimestamp(raw);
  if (!parsed) {
    return undefined;
  }
  if (isUnknownRecordPlaceholder(parsed)) {
    return { kind: 'monthOrEarlier', month: UNKNOWN_RECORD_DISPLAY_MONTH };
  }
  if (field === 'created' && isProductMasterCreatedMonthBound(parsed)) {
    return { kind: 'monthOrEarlier', month: utcMonth(parsed) };
  }
  return { kind: 'exact', date: utcDate(parsed) };
}

/** `YYYY-MM-DD`, `YYYY-MM or earlier`, or undefined when the value is missing or invalid. */
export function formatProductMasterRecordDate(
  raw: unknown,
  field: ProductMasterRecordDateField,
): string | undefined {
  const value = resolveProductMasterRecordDate(raw, field);
  if (!value) {
    return undefined;
  }
  return value.kind === 'exact' ? value.date : `${value.month} or earlier`;
}

function lastDayOfMonth(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  const day = utcFromParts(year, mon, 0, 0, 0, 0).getUTCDate();
  return `${month}-${pad2(day)}`;
}

function dateBound(raw: unknown): string | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const text = raw.trim();
  if (!DATE_ONLY.test(text)) {
    return undefined;
  }
  const parsed = new Date(`${text}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && utcDate(parsed) === text
    ? text
    : undefined;
}

/** Drops bounds that are not valid `YYYY-MM-DD` dates. */
export function normalizeProductMasterRecordDateRange(
  range: unknown,
): ProductMasterRecordDateRange {
  if (!range || typeof range !== 'object' || Array.isArray(range)) {
    return {};
  }
  const rec = range as Record<string, unknown>;
  const from = dateBound(rec.from);
  const to = dateBound(rec.to);
  return { ...(from ? { from } : {}), ...(to ? { to } : {}) };
}

export function hasProductMasterRecordDateRange(range: unknown): boolean {
  const { from, to } = normalizeProductMasterRecordDateRange(range);
  return Boolean(from || to);
}

/**
 * True when the stored value certainly falls inside the range. A "month or earlier"
 * created date only matches an open-ended "on or before" range that covers the whole
 * month; any lower bound excludes it because the real date may be earlier.
 * Missing values never match an active range.
 */
export function productMasterRecordDateInRange(
  raw: unknown,
  field: ProductMasterRecordDateField,
  range: unknown,
): boolean {
  const { from, to } = normalizeProductMasterRecordDateRange(range);
  if (!from && !to) {
    return true;
  }
  const value = resolveProductMasterRecordDate(raw, field);
  if (!value) {
    return false;
  }
  if (value.kind === 'monthOrEarlier') {
    return !from && to !== undefined && lastDayOfMonth(value.month) <= to;
  }
  if (from && value.date < from) {
    return false;
  }
  if (to && value.date > to) {
    return false;
  }
  return true;
}
